import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, map, take } from 'rxjs/operators';
import { environment } from 'oneok/environments/environment';
import { LightboxService, SpinnerService, StorageService, ToastService } from '@core/services';
import { PermissionsService } from '@customer/services';
import posthog from 'posthog-js';
import { Observable, throwError } from 'rxjs';
import { Account as PortalAccount } from '@legacy-models/portal/Account';
import { SessionAuth } from '@legacy-models/auth/user';
import { Contact as ContactModel } from '@legacy-models/portal/Contact';

interface PortalAccountRoleApiItem {
  AccountId?: string | null;
  AccountName?: string | null;
  PortalRole?: {
    Description?: string | null;
    Resources?: unknown[];
  } | null;
  PortalRoleId?: string | null;
  PortalRoleName?: string | null;
}

type AccountResponseWithRoles = PortalAccount & {
  PortalAccountRoles?: PortalAccountRoleApiItem[];
};

@Injectable({
  providedIn: 'root'
})
export class Account {
  private readonly _http = inject(HttpClient);
  private readonly _spinnerSvc = inject(SpinnerService);
  private readonly _router = inject(Router);
  private readonly _lightboxSvc = inject(LightboxService);
  readonly storageSvc = inject(StorageService);
  private readonly _toastSvc = inject(ToastService);
  private readonly _permissionsSvc = inject(PermissionsService);
  baseUrl = environment.apiUrlV2;
  token = '';
  acctId = '';
  acct: AccountResponseWithRoles | null = null;
  noAcct = {
    AccountId: '00000000000000000000000000000000',
    AccountName: 'No Company Selected..',
    BusinessSegments: ['N/A']
  };

  getAccount(id: string): Observable<any> {
    return this._http.get<PortalAccount>(`${this.baseUrl}/accounts/${id}`).pipe(
      catchError((error: HttpErrorResponse) => {
        return throwError(error);
      }),
      map((response: PortalAccount) => {
        return this.formatAccountPortalRoles(response);
      })
    );
  }

  formatAccountPortalRoles(response: AccountResponseWithRoles | null | undefined): Record<string, any> {
    const portalExternalRoles =
      response?.PortalAccountRoles?.map((item: PortalAccountRoleApiItem) => ({
        AccountId: item?.AccountId ?? null,
        AccountName: item?.AccountName ?? null,
        BusinessSegment: 'N/A',
        Description: item?.PortalRole?.Description ?? null,
        PortalRoleId: item?.PortalRoleId ?? null,
        RoleName: item?.PortalRoleName ?? null,
        Resources: item?.PortalRole?.Resources ?? []
      })) || [];

    delete response?.PortalAccountRoles;
    const updatedPayload = {
      ...(response ?? {}),
      PortalRoles: portalExternalRoles
    };
    return updatedPayload as PortalAccount & { PortalRoles: typeof portalExternalRoles };
  }

  getAccounts(): Observable<any> {
    return this._http.get<PortalAccount[]>(`${this.baseUrl}/accounts`);
  }

  getAccountAdministrators(accountId: string): Observable<any> {
    return this._http.get<Pick<ContactModel, 'Email' | 'FullName'>[]>(
      `${this.baseUrl}/accounts/${accountId}/administrators`
    );
  }

  getAccountRoles(accountId: string): Observable<any> {
    return this._http.get<unknown[]>(`${this.baseUrl}/accounts/${accountId}/roles`);
  }

  getContacts(accountId: string): Observable<any> {
    return this._http.get<unknown>(this.baseUrl + '/accounts/' + accountId + '/contacts').pipe(
      map((response: unknown) => response),
      take(1)
    );
  }

  formatGuid(guid: string): string {
    if (guid.length === 32) {
      return guid.replace(/^(.{8})(.{4})(.{4})(.{4})(.{12})$/, '$1-$2-$3-$4-$5');
    }
    return guid; // Already formatted or invalid
  }

  /**
   * @param {string} acctId
   * @param {boolean} [isOnlyCompanyCodes=true] - return only the unique company codes (if false, include BA numbers)
   */

  updateAccountEsaaFlag(acctId: string, isEsaaSigned: boolean): Observable<any> {
    return this._http.put(`${this.baseUrl}/accounts/${acctId}/esaa`, isEsaaSigned);
  }

  //set account dashboard home state based on account and contact Access
  setAccountDashboardHomeRoute(
    acct: Partial<PortalAccount> & { BusinessSegments?: string[] },
    currentUser: SessionAuth & { Contact?: { BusinessSegments?: string[] } }
  ): '/gathering-and-processing' | '/intrastate-pipelines' {
    const isAccountHasNoSegments = !acct.BusinessSegments?.length;
    const isAccountNGGP: boolean = acct.BusinessSegments?.includes('NGGP') ?? false;
    const isAccountNGP: boolean = acct.BusinessSegments?.includes('NGPIntrastate') ?? false;
    const isContactNGGP: boolean =
      (currentUser.Contact?.BusinessSegments?.includes('NGGP') ?? false) || this._permissionsSvc.hasPortalAdminRole();
    const isContactNGP: boolean =
      (currentUser.Contact?.BusinessSegments?.includes('NGPIntrastate') ?? false) ||
      this._permissionsSvc.hasPortalAdminRole();
    const hasUserAndAccountNGGPAccess = (isAccountHasNoSegments && isContactNGGP) || (isAccountNGGP && isContactNGGP);
    const hasUserAndAccountNGPAccess = (isAccountHasNoSegments && isContactNGP) || (isAccountNGP && isContactNGP);

    if ((hasUserAndAccountNGGPAccess && hasUserAndAccountNGPAccess) || hasUserAndAccountNGGPAccess) {
      return '/gathering-and-processing';
    }

    if (hasUserAndAccountNGPAccess) {
      return '/intrastate-pipelines';
    }

    return '/gathering-and-processing';
  }

  isAccountNGPIntastateOnly(acct: Partial<PortalAccount> & { BusinessSegments?: string[] }): boolean {
    const isAccountNGGP = acct.BusinessSegments?.includes('NGGP') ?? false;
    const isAccountNGP = acct.BusinessSegments?.includes('NGPIntrastate') ?? false;
    return isAccountNGP && !isAccountNGGP;
  }

  initializeSessionAccount(currUser: SessionAuth, doPosthogEvt: null | 'login' | 'switch' = null): void {
    if (currUser.HasAccountSelected) {
      this.getAccount(currUser.CurrAccountId ?? '').subscribe({
        next: currentAccount => {
          const companyCodes = currentAccount.CompanyCodes ?? [];
          if (companyCodes.length > 0) {
            currentAccount.CompanyCodes = this._sanitizeCompanyCodesForAccount(companyCodes);
          }
          posthog.group('company', currentAccount.AccountId, {
            name: currentAccount.AccountName
          });
          const routeHome = this.setAccountDashboardHomeRoute(currentAccount, currUser);
          this.storageSvc.storeCurrentAccount(currentAccount);
          this.setAccountAuthorizedHomeRoute(routeHome, doPosthogEvt);
        },
        error: () => {
          this._spinnerSvc.hideSpinnerButton();
          this._toastSvc.dismissAll();
          this._toastSvc.alert(
            'Company Access',
            'There was a problem retrieving your Company details. Ensure you are associated with a company already integrated into the ONEOK Customer Portal.', {
              shouldShowHelpCenter: false,
              shouldShowServiceDesk: true
            }
          );
        }
      });
      //set session for user that has no account association.
    } else {
      const routeHome = this.setAccountDashboardHomeRoute(this.noAcct, currUser);
      if (doPosthogEvt === 'login') {
        posthog.capture('auth_flow:app_login', {
          is_new: !currUser.HasAccountSelected
        });
      } else if (doPosthogEvt === 'switch') {
        posthog.capture('app:switch_account_update');
      }
      this.storageSvc.storeCurrentAccount(this.noAcct);
      this.setAccountAuthorizedHomeRoute(routeHome);
    }
  }

  setAccountAuthorizedHomeRoute(routeHome: string, doPosthogEvt: null | 'login' | 'switch' = null): void {
    document.body.removeAttribute('class');
    sessionStorage.setItem('routeHome', routeHome);
    const returnRoute = sessionStorage.getItem('_returnRoute')?.split(window.location.origin).at(1);
    if (doPosthogEvt === 'login') {
      posthog.capture('auth_flow:app_login', {
        is_new: false
      });
    } else if (doPosthogEvt === 'switch') {
      posthog.capture('app:switch_account_update');
    }
    this._router.navigate([returnRoute ? returnRoute : routeHome]);
    this._lightboxSvc.close();
  }

  private _sanitizeCompanyCodesForAccount(codes: string[]): string[] {
    const sanitizedCodes = codes.map(code => code.trim());
    const hasCombined = sanitizedCodes.includes('OFS/ORM');
    const hasOFS = sanitizedCodes.includes('OFS');
    const hasORM = sanitizedCodes.includes('ORM');

    if (hasCombined || (hasOFS && hasORM)) {
      return ['OFS', 'ORM'];
    }
    if (hasOFS) {
      return ['OFS'];
    }
    if (hasORM) {
      return ['ORM'];
    }
    return sanitizedCodes;
  }
}
